// "Will it fit?" measuring wizard — custom element behavior controller.
// Vanilla JS, no build step. Targets the frozen data-willitfit-* hooks rendered
// by snippets/willitfit.liquid. See willitfit-contract for the full spec.
(function () {
  'use strict';

  // Mirrors the safePublish() bridge in assets/analytics-tracking.js — publishes
  // to the Super App custom Web Pixel (store_visits table) via the md_element_click
  // channel. Wrapped so a missing/late Shopify.analytics can never break the wizard.
  function safePublish(eventName, payload) {
    try {
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (e) {
      /* silent — analytics must never block UX */
    }
  }

  // Shared dual-publish. Every tracking call in this file goes through here:
  // window.dataLayer (GTM/GA4/Elevar) AND the Super App custom Web Pixel
  // (store_visits) via one of the only two subscribed channels. Extracted from
  // three hand-rolled copies that had drifted — pushCompletionAnalytics had no
  // try/catch at all, which contradicted this file's own "analytics must never
  // block UX" invariant. The guard now lives here so no caller can regress it.
  // Render-job pages (?md_render_job=1, the Super App's headless preset
  // render) are bot pageviews: the FAB impression would otherwise count every
  // render as an arm exposure. Mirrors the gate in analytics-tracking.js.
  // Internal (staff) traffic is dropped the same way: window.MD_INTERNAL_TRAFFIC
  // is set inline in <head> by snippets/md-internal-traffic.liquid.
  function isRenderJob() {
    try {
      return (
        new URLSearchParams(window.location.search).get('md_render_job') === '1'
      );
    } catch (e) {
      return false;
    }
  }

  function publishTracking(channel, dataLayerEvent, payload) {
    if (isRenderJob() || window.MD_INTERNAL_TRAFFIC === true) return;
    try {
      var full = Object.assign(
        {
          page_type: document.body.dataset.pageType || '',
          product_id: document.body.dataset.productId || '',
        },
        payload
      );
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: dataLayerEvent }, full));
      }
      safePublish(channel, full);
    } catch (e) {
      /* silent — analytics must never block UX */
    }
  }

  function publishElementClick(payload) {
    publishTracking('md_element_click', 'element_click', payload);
  }

  // Impressions ride md_section_visible, the same channel the declarative
  // [data-track-visible] path uses (analytics-tracking.js:127-131). Its payload
  // carries no element_action — an impression is not an action.
  function publishSectionVisible(payload) {
    publishTracking('md_section_visible', 'section_visible', payload);
  }

  var STORAGE_KEY = 'md_willitfit_v1';
  var POS_KEY = 'md_willitfit_pos_v1';
  var DRAG_THRESHOLD = 6;

  // ── FAB label copy test ────────────────────────────────────────────────
  // ONE arm per visitor, assigned once and held. Deliberately NOT a rotation:
  // a label that auto-updates in place fails WCAG 2.2.2 at Level A, because
  // the "auto-updating" limb of that criterion has NO five-second threshold
  // (only the moving/blinking/scrolling limb does), and a changing accessible
  // name races voice-control users under SC 2.5.3. Do not reintroduce one.
  //
  // To PRUNE a losing arm mid-test, delete its row here. Returning visitors
  // holding a pruned slug fail the findFabArm() lookup and are re-rolled onto
  // a survivor automatically.
  //
  // PRUNED 2026-09-28 at the 3-week decision read (37,079 visitors, 659
  // clickers, visitor-level, SRM p=0.606). Removed because they did not
  // separate from the incumbent at BH q<0.05 over 9 comparisons:
  //   fab_v05_help_me_pick        1.03%  -13% vs control  q=0.53
  //   fab_v07_which_one_fits      1.47%  +25% vs control  q=0.31
  //   fab_v09_not_sure_start_here 1.52%  +29% vs control  q=0.27
  // The six survivors all beat control at q<0.05; the leader
  // (fab_v02_find_my_size, 2.47%, +109%) is q=0.00027. The control row STAYS
  // until a winner is declared - it is the reference the comparison needs.
  // Full read + decision rules: .claude/plans/willitfit-fab-label-copy-test.md
  var FAB_LABEL_KEY = 'md_willitfit_fab_label_v1';
  var FAB_LABEL_ARIA_SUFFIX = ' - Measure your door opening';
  var FAB_LABEL_CONTROL_COPY = 'Will it fit?';
  var FAB_LABEL_VARIANTS = [
    { slug: 'fab_v01_control', copy: 'Will it fit?' },
    { slug: 'fab_v02_find_my_size', copy: 'Find my size' },
    { slug: 'fab_v03_how_do_i_measure', copy: 'How do I measure?' },
    { slug: 'fab_v04_what_size_do_i_need', copy: 'What size do I need?' },
    { slug: 'fab_v06_measure_my_opening', copy: 'Measure my opening' },
    { slug: 'fab_v08_measure_it_right', copy: 'Measure it right' },
    { slug: 'fab_v10_size_me_up', copy: 'Size me up' },
  ];

  // Visitors whose localStorage is unavailable can never be randomised, so
  // they are NOT part of the experiment. Tagged on BOTH the click and the
  // exposure row: tagging only one side would leak their clicks into real
  // control's numerator with no matching denominator, quietly inflating it.
  var FAB_LABEL_FALLBACK_SLUG = 'fab_v01_control_fallback';

  // Once the wizard is complete the FAB stops being a copy test and becomes a
  // "view my saved size" receipt. A click on THAT has nothing to do with which
  // phrase persuaded anyone, so it must not report an arm slug — otherwise
  // repeat-open clicks inflate whichever arm the visitor happened to draw, and
  // repeat-open behaviour plausibly correlates with the arm itself.
  var FAB_LABEL_DONE_SLUG = 'fab_saved_receipt';

  // Once per PAGEVIEW, not once per element instance. Held on `document`
  // rather than in a module variable: identical semantics (a document IS a
  // pageview, so it survives any disconnect/reconnect within the page and
  // resets naturally on real navigation), but observable to tests and to
  // debugging, which a closure variable is not.
  var FAB_EXPOSURE_FLAG = '__mdWillItFitFabExposureFired';

  function findFabArm(slug) {
    if (!slug) return null;
    for (var i = 0; i < FAB_LABEL_VARIANTS.length; i++) {
      if (FAB_LABEL_VARIANTS[i].slug === slug) return FAB_LABEL_VARIANTS[i];
    }
    return null;
  }

  // Auto-dock: default the FAB just above the HubSpot chat launcher so the two
  // dot-gripped widgets read as a draggable-tools cluster (teaches people the
  // FAB moves). The launcher is a DEFERRED third-party iframe wrapper injected
  // async — it mounts AFTER we boot and inflates from 0-height — so "does it
  // exist?" races and loses. See initChatDock() for the belt-and-suspenders.
  // Since 2026-10-09 HubSpot chat is click-to-chat: the theme's own
  // #md-chat-launcher (snippets/md-chat-launcher.liquid) holds that slot and is
  // listed FIRST, so it wins whenever it is in the DOM (querySelector returns
  // the first match in document order, and it renders before HubSpot appends
  // its container). While the chat is open it is hidden (0x0) and skipped.
  var CHAT_SEL = '#md-chat-launcher, #hubspot-messages-iframe-container';
  var CHAT_GAP = 28; // px gap between the FAB's bottom and the launcher's top — wide enough for the "Drag me" hint text that sits UNDER the FAB (see .willitfit__fab-hint)
  var CHAT_OPEN_MAXH = 120; // px: taller than this = the OPEN chat panel, not the launcher — skip (measured launcher ≈96px)
  var DOCK_MIN_LEFT = 16; // px, matches --space-sm: align to the launcher's left edge but never tighter than the theme gutter (HubSpot's container is flush at left:0 while its visible bubble sits ~16px in)
  var DOCK_POLL_MS = [0, 500, 1000, 2000, 4000]; // capped backoff safety net (self-terminating)

  // STEP 0 ("mount") and STEP 1 ("path") ask Mount Type and Path as two
  // separate questions — independent of each other (either mount can be
  // existing-door or new-construction), but mount comes first because
  // everything downstream branches off IT, not path: Surface Mount has no
  // jamb (it mounts to the wall surface, not into it), so its step list
  // omits 'jamb' entirely — getSteps() picks the active array.
  var STEPS_FLUSH = ['mount', 'path', 'width', 'height', 'jamb', 'handing', 'results'];
  var STEPS_SURFACE = ['mount', 'path', 'width', 'height', 'handing', 'results'];
  var MEASURE_STEPS = ['width', 'height', 'jamb'];
  var WHOLE_REGEX = /^\d{1,3}$/;
  // Door Handing: the combined term Murphy Door PDPs use for hinge side +
  // swing direction together (see ptw-widget.liquid "Door handing" setting).
  // Some PDPs ask for "Hinges On" + "Swing Direction" as two separate APO
  // option groups (quick-config.js); others show one combined 4-value
  // dropdown. The wizard states the answer both ways so it applies either
  // way the customer's specific product page phrases it.
  var HANDING_VALUES = ['right-in', 'right-out', 'left-in', 'left-out'];
  var OUTSWING_VALUES = ['right-out', 'left-out'];
  var HANDING_META = {
    'right-in': { label: 'Right-Hand In-Swing', hinge: 'Right', swing: 'In-Swing' },
    'right-out': { label: 'Right-Hand Out-Swing', hinge: 'Right', swing: 'Out-Swing' },
    'left-in': { label: 'Left-Hand In-Swing', hinge: 'Left', swing: 'In-Swing' },
    'left-out': { label: 'Left-Hand Out-Swing', hinge: 'Left', swing: 'Out-Swing' },
  };
  // Standard door sizes (whole inches), Flush Mount only. The newframe path
  // measures the rough opening, which is the door + 2", so its window shifts
  // up by 2 inches — see computeDoorAndRO's 'flush' branch. Surface Mount
  // uses SURFACE_RANGES instead (see below): it has no jamb/shim tolerance,
  // so its range is keyed by measure only, not by path.
  var RANGES = {
    existing: { width: [24, 48], height: [36, 96] },
    newframe: { width: [26, 50], height: [38, 98] },
  };

  // Surface Mount's advisory range for the soft "outside our standard sizes"
  // note — same range for both paths, since computeDoorAndRO no longer shifts
  // Surface Mount's entered value by path (see below). Width mirrors
  // SURFACE_SIZE_TIERS' full 24-72" FO coverage (single door + French door);
  // height allows the same ±1" tolerance as SURFACE_FO_HEIGHT_TOLERANCE.
  var SURFACE_RANGES = {
    width: [24, 72],
    height: [79, 81],
  };

  // Copy for the width/height "newframe" hint + input-prompt paragraphs,
  // overwritten at render time based on mount (see setupMeasurementStep).
  // Flush Mount's copy matches the frozen Liquid markup verbatim — Surface
  // Mount's asks for a planned FINISHED opening instead of a literal
  // stud-to-stud rough measurement, since Surface Mount has no jamb/shim
  // convention to convert one into the other (see computeDoorAndRO).
  var NEWFRAME_MEASURE_COPY = {
    flush: {
      width: {
        hint: 'Measure the rough opening width, stud to stud.',
        prompt: 'Enter your rough opening width below',
      },
      height: {
        hint: 'Measure the rough opening from the floor to the header.',
        prompt: 'Enter your rough opening height below',
      },
    },
    surface: {
      width: {
        hint:
          "Plan the finished opening width you'll frame, drywall, and trim to (not the rough stud-to-stud measurement).",
        prompt: 'Enter your planned finished opening width below',
      },
      height: {
        hint:
          "Plan the finished opening height you'll frame, drywall, and trim to (floor to header).",
        prompt: 'Enter your planned finished opening height below',
      },
    },
  };

  // Surface Mount "Size" option — captured live via mwApoInit for
  // `surface-mount-door` (single door, product 8321885569069, APO option
  // o_3332044 "Size") on 2026-07-21; confirmed identical +8"/89-1/8" formula
  // against `surface-mounted-french-door`'s native Size variants. Unit width
  // = Finished Opening width + 8"; height is fixed at 89-1/8" regardless of
  // tier — Surface Mount has no continuous height customization, it assumes
  // an 80" FO height.
  //
  // Merges BOTH real Surface Mount catalog SKUs, sold through the same
  // /collections/surface-mount-tag this wizard links to: the single door
  // (24/28/30/32/36" FO) and the Surface Mounted French Door (product
  // 7729266065453, 48/60/72" FO -> 56/68/80" unit, per its native Size
  // variant titles e.g. "56\" x 89-1/8\" (For 48\" x 80\" Finished
  // Opening)"). Before this merge, a 48-72" opening fell into the gap
  // between the single door's largest tier and "custom size, call us" even
  // though the French Door already covers it as a standard in-stock SKU —
  // findSurfaceSizeTier's round-up-to-nearest logic picks the right one
  // automatically once both tier sets are in the same sorted array.
  var SURFACE_SIZE_TIERS = [
    { foWidth: 24, unitWidth: 32 },
    { foWidth: 28, unitWidth: 36 },
    { foWidth: 30, unitWidth: 38 },
    { foWidth: 32, unitWidth: 40 },
    { foWidth: 36, unitWidth: 44 },
    { foWidth: 48, unitWidth: 56 },
    { foWidth: 60, unitWidth: 68 },
    { foWidth: 72, unitWidth: 80 },
  ];
  var SURFACE_UNIT_HEIGHT_STR = '89 1/8"';
  var SURFACE_FO_HEIGHT = 80;
  var SURFACE_FO_HEIGHT_TOLERANCE = 1; // inches

  function gcd(a, b) {
    return b === 0 ? a : gcd(b, a % b);
  }

  function simplifyFraction(sixteenths) {
    if (!sixteenths) return null;
    var g = gcd(sixteenths, 16);
    return sixteenths / g + '/' + 16 / g;
  }

  function formatMeasurement(wholeNum, sixteenths) {
    var fracStr = simplifyFraction(sixteenths);
    if (wholeNum > 0 && fracStr) return wholeNum + ' ' + fracStr + '"';
    if (wholeNum > 0 && !fracStr) return wholeNum + '"';
    if (wholeNum === 0 && fracStr) return fracStr + '"';
    return '0"';
  }

  // Rounds UP to the first catalog tier that still covers the measured
  // Finished Opening width — never recommend a unit narrower than the
  // opening. Returns null when the opening is wider than the largest
  // catalog tier (needs a custom quote).
  function findSurfaceSizeTier(foWidthInches) {
    if (!(foWidthInches > 0)) return null;
    for (var i = 0; i < SURFACE_SIZE_TIERS.length; i++) {
      if (SURFACE_SIZE_TIERS[i].foWidth >= foWidthInches) return SURFACE_SIZE_TIERS[i];
    }
    return null;
  }

  // Shared by isComplete() and loadState()'s legacy-session detection —
  // both need the same "is this a real, non-zero measurement" definition.
  // Named -Value to avoid colliding with the instance method of the same
  // base name below, which validates a step name against DOM state, not
  // a {whole, frac} value object.
  function isValidMeasurementValue(m) {
    if (!m) return false;
    var whole = parseInt(m.whole, 10);
    var six = parseInt((m.frac || '0/16').split('/')[0], 10) || 0;
    if (isNaN(whole) || whole < 0 || whole > 199) return false;
    return whole > 0 || six > 0;
  }

  function defaultValues() {
    return {
      width: { whole: '', frac: '0/16' },
      height: { whole: '', frac: '0/16' },
      jamb: { whole: '', frac: '0/16' },
      handing: '',
    };
  }

  class WillItFitEl extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      if (!this.querySelector('[data-willitfit-open]')) return;
      this._connected = true;

      this.isOpen = false;
      this.lastOpener = null;
      this._durationMs = null;
      this._handingPlayTimeouts = [];

      this.overlayEl = this.querySelector('[data-willitfit-overlay]');
      this.modalEl =
        this.querySelector('#willitfit-modal') ||
        this.querySelector('.willitfit__modal');
      this.closeBtn = this.querySelector('[data-willitfit-close]');
      this.backBtn = this.querySelector('[data-willitfit-back]');
      this.nextBtn = this.querySelector('[data-willitfit-next]');
      this.restartBtn = this.querySelector('[data-willitfit-restart]');
      this.resultsHeading = this.querySelector('#willitfit-h-results');
      this.mountButtons = Array.prototype.slice.call(
        this.querySelectorAll('[data-willitfit-mount]')
      );
      this.pathButtons = Array.prototype.slice.call(
        this.querySelectorAll('[data-willitfit-path]')
      );
      this.progressFill = this.querySelector('[data-willitfit-progressfill]');
      this.progressText = this.querySelector('[data-willitfit-progresstext]');
      this.progressTrack = this.querySelector('.willitfit__progress-track');
      this.stepSections = Array.prototype.slice.call(
        this.querySelectorAll('[data-willitfit-step]')
      );
      this.hintEls = Array.prototype.slice.call(
        this.querySelectorAll('[data-willitfit-hint]')
      );
      this.promptEls = Array.prototype.slice.call(
        this.querySelectorAll('[data-willitfit-prompt]')
      );
      this.outEls = {
        width: this.querySelector('[data-willitfit-out="width"]'),
        height: this.querySelector('[data-willitfit-out="height"]'),
        jamb: this.querySelector('[data-willitfit-out="jamb"]'),
        handing: this.querySelector('[data-willitfit-out="handing"]'),
      };
      this.errorEls = {
        width: this.querySelector('[data-willitfit-error="width"]'),
        height: this.querySelector('[data-willitfit-error="height"]'),
        jamb: this.querySelector('[data-willitfit-error="jamb"]'),
      };
      this.softNoteEls = {
        width: this.querySelector('[data-willitfit-softnote="width"]'),
        height: this.querySelector('[data-willitfit-softnote="height"]'),
      };
      this.jambButtons = Array.prototype.slice.call(
        this.querySelectorAll('[data-willitfit-jamb-choice]')
      );
      this.jambRowEl = this.querySelector('[data-willitfit-jamb-row]');
      this.handingButtons = Array.prototype.slice.call(
        this.querySelectorAll('[data-willitfit-handing-choice]')
      );
      this.framingNoteEls = Array.prototype.slice.call(
        this.querySelectorAll('[data-willitfit-framing-note]')
      );
      this.shopLinkEls = Array.prototype.slice.call(
        this.querySelectorAll('[data-willitfit-shoplink]')
      );
      this.surfaceOutswingNoteEl = this.querySelector(
        '[data-willitfit-surface-outswing-note]'
      );
      this.pausePreviewsBtn = this.querySelector('[data-willitfit-pause-previews]');
      // WCAG 2.2.2 (Pause, Stop, Hide) mechanism for the 4 autoplaying,
      // looping handing-preview videos — honors the OS-level reduced-motion
      // preference as a sane starting default, and lets any visitor toggle
      // it regardless. The tiles themselves stay native <button> elements
      // (needed for click-to-select), so native <video controls> inside them
      // isn't an option — nested interactive content would both be invalid
      // HTML and hijack the tile's click target.
      this.previewsPaused =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.footerEl = this.querySelector('.willitfit__footer');
      this.doneBtn = this.querySelector('[data-willitfit-done]');
      // Mount type of the product this page sells, emitted by
      // snippets/willitfit.liquid from product.tags. Only openExternally()
      // reads it — the FAB and the [data-willitfit-open] delegation stay
      // page-agnostic on purpose. Validated here rather than at each use so a
      // stray/typo'd attribute value degrades to "no preset", never to a
      // corrupt state.mount that getSteps() would branch on.
      var pageMount = this.getAttribute('data-willitfit-page-mount');
      this.pageMount =
        pageMount === 'flush' || pageMount === 'surface' ? pageMount : null;

      this.fabButton = this.querySelector('[data-willitfit-open]');
      this.fabLabel = this.querySelector('[data-willitfit-fablabel]');
      this.fabBadge = this.querySelector('[data-willitfit-fabbadge]');
      this.fabHint = this.querySelector('[data-willitfit-fabhint]');

      this.state = this.loadState();

      // Before the first updateFabState() below, so no arm flashes and is
      // then overwritten by the hardcoded control string.
      this.assignFabLabelArm();

      // Delegated listeners scoped to this element (close/back/next/restart/choice + inputs).
      this._boundClick = this.handleClick.bind(this);
      this._boundInput = this.handleInput.bind(this);
      this._boundChange = this.handleChange.bind(this);
      this.addEventListener('click', this._boundClick);
      this.addEventListener('input', this._boundInput);
      this.addEventListener('change', this._boundChange);

      if (this.overlayEl) {
        this._boundOverlayClick = this.handleOverlayClick.bind(this);
        this.overlayEl.addEventListener('click', this._boundOverlayClick);
      }

      // Document-level delegation so external openers (e.g. the PDP link,
      // which lives outside <will-it-fit>) open the same modal.
      this._boundDocumentClick = this.handleDocumentClick.bind(this);
      document.addEventListener('click', this._boundDocumentClick);

      this._boundDocumentKeydown = this.handleDocumentKeydown.bind(this);
      document.addEventListener('keydown', this._boundDocumentKeydown);

      this._boundFallbackKeydown = this.handleFallbackKeydown.bind(this);

      // Public handle for same-page consumers that CANNOT query the DOM for
      // us. The configurator's Phase 2 modules are forbidden direct DOM access
      // (CCP rule 4, enforced by .claude/hooks/md-config-ccp-validator.py), so
      // md-config-support-willitfit-prefill.js reads the wizard here instead of
      // via document.querySelector('will-it-fit'). Deliberately NOT on
      // window.MurphyDoorConfigurator: this widget renders on every template
      // and must not depend on the configurator existing.
      //
      // Assigned only after the refs above are wired, so anything that finds
      // this global finds a usable element — and consumers still probe for the
      // method they need rather than trusting the global's presence.
      window.MurphyDoorWillItFit = this;

      this.initDrag();
      this.updateFabState();
    }

    disconnectedCallback() {
      // connectedCallback bails before initialising anything when the markup
      // carries no opener, so an element that never connected has no
      // _handingPlayTimeouts for clearPendingHandingPlays() to iterate — it
      // threw an uncaught TypeError out of the custom-element callback on
      // removal. Nothing below has anything to undo in that state.
      if (!this._connected) return;

      if (this._boundDocumentClick) {
        document.removeEventListener('click', this._boundDocumentClick);
      }
      if (this._boundDocumentKeydown) {
        document.removeEventListener('keydown', this._boundDocumentKeydown);
      }
      if (this._boundFallbackKeydown) {
        document.removeEventListener('keydown', this._boundFallbackKeydown);
      }
      if (this._boundResize) {
        window.removeEventListener('resize', this._boundResize);
      }
      this.teardownChatDock();
      this.teardownControlsStandDown();
      this.clearPendingHandingPlays();
      if (window.MurphyDoorWillItFit === this) {
        window.MurphyDoorWillItFit = null;
      }
      this._connected = false;
    }

    /* ---------------------------------------------------------------- */
    /* Draggable FAB (pointer/touch, position persisted)                 */
    /* ---------------------------------------------------------------- */

    initDrag() {
      // The draggable unit is the wrap (dark circle button + 6-dot grip).
      this.fabEl = this.querySelector('.willitfit__fab-wrap');
      if (!this.fabEl) return;
      this.dragState = null;
      this._justDragged = false;
      // True once the visitor owns the position (restored from a prior drag, or
      // dragged this session). While false, we auto-dock above the chat.
      this._userPositioned = this.restoreFabPosition();
      this._boundPointerDown = this.onFabPointerDown.bind(this);
      this._boundPointerMove = this.onFabPointerMove.bind(this);
      this._boundPointerUp = this.onFabPointerUp.bind(this);
      this._boundResize = this.onFabResize.bind(this);
      this.fabEl.addEventListener('pointerdown', this._boundPointerDown);
      window.addEventListener('resize', this._boundResize);
      if (this._userPositioned) {
        // They've already moved it — they know it drags. No hint, no docking.
        this.dismissFabHint();
      } else {
        this.initChatDock();
      }
      this.initControlsStandDown();
    }

    /* ---------------------------------------------------------------- */
    /* Stand down over product-option controls on phones                 */
    /* ---------------------------------------------------------------- */

    // On a phone the selects in the product form run edge to edge (x 10-380 at
    // 390 px), so wherever the FAB docks it sits on top of one of them while the
    // shopper scrolls through the options. The 2026-10-13 iPhone walk found it
    // (with the chat launcher) covering the Door Depth and Extra Shelves values
    // on the Hidden Bookcase Door. So while the product form (MageWorx APO) or
    // the configurator's option panel is on screen, the FAB steps aside; it is
    // back over the gallery, the description and the reviews. The in-form
    // "Will it fit?" link above the options opens the same wizard.
    // visibility (CSS, willitfit__fab-wrap--over-controls) keeps the rect, the
    // saved/docked position and the drag math untouched, the same convention as
    // the ready-badge stand-down. Phones only; desktop has room beside the form.
    initControlsStandDown() {
      if (!this.fabEl || typeof window.IntersectionObserver !== 'function') return;
      if (typeof window.matchMedia !== 'function') return;
      var targets = document.querySelectorAll(
        'product-form form[action*="/cart/add"], #ConfiguratorOptions'
      );
      if (!targets.length) return;
      var mq = window.matchMedia('(max-width: 749px)');
      var fab = this.fabEl;
      var onScreen = new Set();
      var apply = function () {
        fab.classList.toggle(
          'willitfit__fab-wrap--over-controls',
          mq.matches && onScreen.size > 0
        );
      };
      this._controlsIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) onScreen.add(entry.target);
          else onScreen.delete(entry.target);
        });
        apply();
      });
      targets.forEach(
        function (t) {
          this._controlsIO.observe(t);
        }.bind(this)
      );
      this._controlsMQ = mq;
      this._controlsApply = apply;
      if (typeof mq.addEventListener === 'function') mq.addEventListener('change', apply);
    }

    teardownControlsStandDown() {
      if (this._controlsIO) {
        this._controlsIO.disconnect();
        this._controlsIO = null;
      }
      if (this._controlsMQ && typeof this._controlsMQ.removeEventListener === 'function') {
        this._controlsMQ.removeEventListener('change', this._controlsApply);
      }
      this._controlsMQ = null;
      this._controlsApply = null;
    }

    onFabPointerDown(e) {
      // Reset here (not on a timer) so a delayed post-drag click can't slip
      // through and open the modal — the flag only clears on a NEW interaction.
      this._justDragged = false;
      if (typeof e.button === 'number' && e.button !== 0) return;
      var rect = this.fabEl.getBoundingClientRect();
      this.dragState = {
        startX: e.clientX,
        startY: e.clientY,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        pointerId: e.pointerId,
        moved: false,
      };
      document.addEventListener('pointermove', this._boundPointerMove);
      document.addEventListener('pointerup', this._boundPointerUp);
      document.addEventListener('pointercancel', this._boundPointerUp);
    }

    onFabPointerMove(e) {
      if (!this.dragState) return;
      var dx = e.clientX - this.dragState.startX;
      var dy = e.clientY - this.dragState.startY;
      if (!this.dragState.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
      if (!this.dragState.moved) {
        this.dragState.moved = true;
        this.fabEl.classList.add('willitfit__fab--dragging');
        try {
          this.fabEl.setPointerCapture(this.dragState.pointerId);
        } catch (err) {
          // pointer capture is best-effort
        }
      }
      if (e.cancelable) e.preventDefault();
      this.setFabPosition(
        e.clientX - this.dragState.offsetX,
        e.clientY - this.dragState.offsetY
      );
    }

    onFabPointerUp() {
      document.removeEventListener('pointermove', this._boundPointerMove);
      document.removeEventListener('pointerup', this._boundPointerUp);
      document.removeEventListener('pointercancel', this._boundPointerUp);
      if (!this.dragState) return;
      var moved = this.dragState.moved;
      this.dragState = null;
      if (!moved) return;
      this.fabEl.classList.remove('willitfit__fab--dragging');
      // Suppress the click that fires right after a drag so it doesn't open the
      // modal. Cleared on the next pointerdown (see onFabPointerDown).
      this._justDragged = true;
      // The visitor now owns the position: stop auto-docking (never yank it out
      // from under them) and retire the drag hint (they've learned it moves).
      this._userPositioned = true;
      this.teardownChatDock();
      this.dismissFabHint();
      this.persistFabPosition();
    }

    setFabPosition(left, top) {
      if (!this.fabEl) return;
      var rect = this.fabEl.getBoundingClientRect();
      var maxLeft = Math.max(4, window.innerWidth - rect.width - 4);
      var maxTop = Math.max(4, window.innerHeight - rect.height - 4);
      left = Math.max(4, Math.min(left, maxLeft));
      top = Math.max(4, Math.min(top, maxTop));
      this.fabEl.style.left = left + 'px';
      this.fabEl.style.top = top + 'px';
      this.fabEl.style.right = 'auto';
      this.fabEl.style.bottom = 'auto';
    }

    persistFabPosition() {
      try {
        var rect = this.fabEl.getBoundingClientRect();
        window.localStorage.setItem(
          POS_KEY,
          JSON.stringify({ left: Math.round(rect.left), top: Math.round(rect.top) })
        );
      } catch (e) {
        // ignore quota/private-mode
      }
    }

    // Returns true if a saved position was applied (visitor owns the position).
    restoreFabPosition() {
      try {
        var raw = window.localStorage.getItem(POS_KEY);
        if (!raw) return false;
        var pos = JSON.parse(raw);
        if (pos && typeof pos.left === 'number' && typeof pos.top === 'number') {
          this.setFabPosition(pos.left, pos.top);
          return true;
        }
      } catch (e) {
        // ignore
      }
      return false;
    }

    onFabResize() {
      if (this._userPositioned) {
        this.clampFabToViewport();
      } else {
        // Auto-docked: the chat launcher moves/reflows on resize — re-dock to it.
        this.scheduleChatDock();
      }
    }

    clampFabToViewport() {
      // Only re-clamp when the FAB has been custom-positioned via drag.
      if (!this.fabEl || !this.fabEl.style.left) return;
      var rect = this.fabEl.getBoundingClientRect();
      this.setFabPosition(rect.left, rect.top);
    }

    /* ---------------------------------------------------------------- */
    /* Auto-dock above the HubSpot chat launcher (belt-and-suspenders)   */
    /* ---------------------------------------------------------------- */

    // Runs only when the visitor has NOT positioned the FAB. The launcher is a
    // deferred third-party iframe wrapper that mounts async and inflates from
    // 0-height AFTER we boot, so a one-shot check races and loses. Layered
    // defense so no overlap window can exist:
    //   BELT       — the CSS default already rests bottom-left ABOVE the
    //                launcher zone, so even if none of this runs we never overlap.
    //   SUSPENDERS — (1) trust rendered SIZE, never mere existence; (2) ignore
    //                the OPEN chat panel (shares the same id); (3) a
    //                MutationObserver catches the launcher's late injection;
    //                (4) a ResizeObserver re-docks on every settle — this is what
    //                beats the "loads later + inflates" race; (5) a capped
    //                backoff poll as a last resort if an observer ever misses.
    initChatDock() {
      this._docking = true;
      this._dockRaf = 0;
      this._dockTimers = [];
      this._chatMO = null;
      this._chatRO = null;
      this._bodyClassMO = null;
      this.tryDockToChat();
      this.observeChatArrival();
      var self = this;
      // The launcher MOVES (not resizes) when the PDP sticky bar toggles
      // `body.pdp-sticky-bar-active` (md-chat-launcher.css lifts it to
      // bottom:110px). The ResizeObserver above never fires for a pure move,
      // so the FAB stayed docked at the old spot and ended up INSIDE the lifted
      // card. Re-dock on any body class change; scheduleChatDock coalesces.
      if (typeof MutationObserver !== 'undefined' && document.body) {
        this._bodyClassMO = new MutationObserver(function () {
          self.scheduleChatDock();
        });
        this._bodyClassMO.observe(document.body, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
      DOCK_POLL_MS.forEach(function (ms) {
        var t = window.setTimeout(function () {
          self.observeChatArrival();
          self.tryDockToChat();
        }, ms);
        self._dockTimers.push(t);
      });
    }

    // Wire observers the moment the launcher is in the DOM. Idempotent: the
    // ResizeObserver is only attached once, and finding it stops the body watch.
    observeChatArrival() {
      if (!this._docking) return;
      var chat = document.querySelector(CHAT_SEL);
      if (chat) {
        if (!this._chatRO && typeof ResizeObserver !== 'undefined') {
          var self = this;
          this._chatRO = new ResizeObserver(function () {
            self.scheduleChatDock();
          });
          this._chatRO.observe(chat);
        }
        if (this._chatMO) {
          this._chatMO.disconnect();
          this._chatMO = null;
        }
        return;
      }
      // Not injected yet — watch <body> for its arrival (HubSpot appends it as a
      // direct child of body). The capped poll is the backstop if it ever nests.
      if (!this._chatMO && typeof MutationObserver !== 'undefined') {
        var that = this;
        this._chatMO = new MutationObserver(function () {
          if (document.querySelector(CHAT_SEL)) {
            that.observeChatArrival(); // promotes to ResizeObserver + stops this watch
            that.scheduleChatDock();
          }
        });
        this._chatMO.observe(document.body, { childList: true });
      }
    }

    // rAF-coalesced so a burst of observer callbacks does at most one layout read.
    scheduleChatDock() {
      if (!this._docking || this._dockRaf) return;
      var self = this;
      this._dockRaf = requestAnimationFrame(function () {
        self._dockRaf = 0;
        self.tryDockToChat();
      });
    }

    tryDockToChat() {
      if (!this._docking || this._userPositioned || !this.fabEl) return;
      var chat = document.querySelector(CHAT_SEL);
      if (!chat) return;
      var c = chat.getBoundingClientRect();
      // (1) Trust rendered size, not mere existence — the pre-init launcher is
      // 0-height / hidden.
      if (c.width <= 0 || c.height <= 0) return;
      if (c.bottom <= 0 || c.top >= window.innerHeight) return; // off-screen
      // (2) The OPEN HubSpot chat panel shares its container id — don't rocket
      // the FAB to the top of a ~600px panel. Only dock to the collapsed
      // launcher. Our own #md-chat-launcher card is always a launcher (it hides
      // while the chat is open) and is ~134px tall with its privacy note, so the
      // height cap must not apply to it.
      if (chat.id !== 'md-chat-launcher' && c.height > CHAT_OPEN_MAXH) return;
      var f = this.fabEl.getBoundingClientRect();
      // Sit just above the launcher, left edges aligned (but never tighter than
      // the theme gutter, since the launcher's container is flush at left:0 while
      // its visible bubble is inset ~16px). setFabPosition clamps to the viewport
      // and deliberately does NOT persist — this stays a live default, never
      // masquerading as a saved user choice.
      this.setFabPosition(Math.max(c.left, DOCK_MIN_LEFT), c.top - f.height - CHAT_GAP);
    }

    teardownChatDock() {
      this._docking = false;
      if (this._dockRaf) {
        cancelAnimationFrame(this._dockRaf);
        this._dockRaf = 0;
      }
      if (this._dockTimers) {
        this._dockTimers.forEach(function (t) {
          window.clearTimeout(t);
        });
        this._dockTimers = [];
      }
      if (this._chatMO) {
        this._chatMO.disconnect();
        this._chatMO = null;
      }
      if (this._chatRO) {
        this._chatRO.disconnect();
        this._chatRO = null;
      }
      if (this._bodyClassMO) {
        this._bodyClassMO.disconnect();
        this._bodyClassMO = null;
      }
    }

    dismissFabHint() {
      if (this.fabHint && !this.fabHint.hidden) {
        this.fabHint.hidden = true;
      }
    }

    /* ---------------------------------------------------------------- */
    /* Step sequencing — mount-dependent (Surface Mount skips 'jamb')    */
    /* ---------------------------------------------------------------- */

    getSteps() {
      return this.state.mount === 'surface' ? STEPS_SURFACE : STEPS_FLUSH;
    }

    /* ---------------------------------------------------------------- */
    /* localStorage                                                      */
    /* ---------------------------------------------------------------- */

    loadState() {
      var defaults = { path: null, mount: null, step: 0, values: defaultValues() };
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaults;
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return defaults;

        var legacyPath =
          parsed.path === 'existing' || parsed.path === 'newframe';
        // Pre-mount-split saves have no `mount` field. Every one of those
        // sessions was necessarily Flush Mount (Surface didn't exist yet) —
        // defaulting to null here would fail the isComplete()/corruption-guard
        // checks below and silently drop a returning customer's saved result.
        var mount =
          parsed.mount === 'flush' || parsed.mount === 'surface'
            ? parsed.mount
            : legacyPath
              ? 'flush'
              : null;
        var steps = mount === 'surface' ? STEPS_SURFACE : STEPS_FLUSH;

        var step =
          typeof parsed.step === 'number' &&
          parsed.step >= 0 &&
          parsed.step <= steps.length - 1
            ? parsed.step
            : 0;
        var path =
          parsed.path === 'existing' || parsed.path === 'newframe'
            ? parsed.path
            : null;
        var values = defaultValues();
        if (parsed.values && typeof parsed.values === 'object') {
          MEASURE_STEPS.forEach(function (m) {
            var v = parsed.values[m];
            if (v && typeof v === 'object') {
              values[m] = {
                whole: String(v.whole || ''),
                frac: String(v.frac || '0/16'),
              };
            }
          });
          var handingVal = parsed.values.handing;
          // A saved In-Swing selection is invalid if the mount is Surface
          // (Out-Swing only) — drop it rather than carry a stale answer.
          if (
            HANDING_VALUES.indexOf(handingVal) !== -1 &&
            !(mount === 'surface' && OUTSWING_VALUES.indexOf(handingVal) === -1)
          ) {
            values.handing = handingVal;
          }
        }
        // Corruption guard: can't be on/past 'path' (index 1) without mount
        // chosen, and can't be on/past 'width' (index 2) without both.
        if (step === 1 && !mount) step = 0;
        else if (step >= 2 && (!mount || !path)) step = 0;

        // Pre-PR saves predate the handing step entirely, so a genuine
        // legacy session can never satisfy isComplete()'s handingOk check
        // even after `mount` is defaulted above. Detected only when mount
        // was originally missing (the reliable legacy signal) AND
        // width/height/jamb were already a complete, valid answer - not for
        // a real in-progress post-PR session that simply hasn't reached the
        // handing step yet (that one correctly stays "incomplete"). Sticky
        // via parsed.legacyHandingExempt so it survives being re-persisted
        // with an explicit `mount` value on the next save (see
        // onChooseMount(), which clears it if the customer actively
        // re-engages with the wizard rather than just reopening their badge).
        var legacyHandingExempt =
          parsed.legacyHandingExempt === true ||
          (legacyPath &&
            parsed.mount == null &&
            isValidMeasurementValue(values.width) &&
            isValidMeasurementValue(values.height) &&
            values.jamb &&
            (values.jamb.whole === '4' || values.jamb.whole === '6'));

        return {
          path: path,
          mount: mount,
          step: step,
          values: values,
          legacyHandingExempt: legacyHandingExempt,
        };
      } catch (e) {
        return defaults;
      }
    }

    persistState() {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch (e) {
        // Ignore quota / private-mode errors — the widget still works, it just won't bookmark.
      }
    }

    /* ---------------------------------------------------------------- */
    /* Open / close                                                      */
    /* ---------------------------------------------------------------- */

    handleDocumentClick(e) {
      // A drag just ended — swallow the synthetic click so it doesn't open.
      if (this._justDragged) return;
      var opener =
        e.target && e.target.closest ? e.target.closest('[data-willitfit-open]') : null;
      if (!opener) return;
      this.openModal(opener);
    }

    handleDocumentKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        this.closeModal();
      }
    }

    handleOverlayClick(e) {
      if (e.target === this.overlayEl) this.closeModal();
    }

    /**
     * PUBLIC ENTRY POINT for openers that cannot use the document-level
     * [data-willitfit-open] delegation.
     *
     * The configurator's "how to measure" tape icons are the first such
     * caller: the tooltip manager must stopPropagation() on their clicks
     * (a click that escapes one of those icons reaches the category card
     * handler in md-config-core-event-handler.js), and stopPropagation kills
     * the delegation this widget listens on. So they call this instead.
     *
     * Being a real method is also the CAPABILITY CHECK callers need.
     * `document.querySelector('will-it-fit')` is truthy even when
     * willitfit.js never loaded — the element is a bare HTMLElement then,
     * exactly the presence-vs-upgrade trap that silently ate add-to-cart in
     * the cart-drawer incident (see CLAUDE.md). `typeof el.openExternally ===
     * 'function'` cannot be fooled that way, and the boolean return lets a
     * caller fall back to its own behaviour when the widget is present but
     * not wired up.
     *
     * Unlike the FAB, this path ANSWERS THE MOUNT STEP from the page's
     * product tag and lands the customer on 'path'. A shopper who is standing
     * on a Flush Mount PDP has already told us the answer to step 0.
     *
     * @param {{opener?: Element, source?: string, label?: string}} [options]
     * @returns {boolean} true when the wizard handled it
     */
    openExternally(options) {
      // Not _connected means connectedCallback bailed (no opener in the
      // markup) and none of the element refs below exist yet.
      if (!this._connected) return false;

      var opts = options || {};
      var mount = this.pageMount;
      var hasPreset = mount === 'flush' || mount === 'surface';

      if (hasPreset && this.state.mount !== mount) {
        // Same invalidations onChooseMount() performs, and for the same
        // reasons: Surface Mount offers no In-Swing hardware, and a mount
        // switch means the saved answers are being re-walked rather than
        // restored, so the legacy handing exemption must not carry forward.
        this.state.mount = mount;
        if (
          mount === 'surface' &&
          OUTSWING_VALUES.indexOf(this.state.values.handing) === -1
        ) {
          this.state.values.handing = '';
        }
        this.state.legacyHandingExempt = false;
      }

      if (this.isOpen) {
        // Already open (programmatic caller only — the icons sit behind the
        // overlay). goToStep owns leaveStep/persist/render/focus/tracking.
        if (hasPreset) this.goToStep(this.getSteps().indexOf('path'));
      } else {
        if (hasPreset) {
          // Set the step BEFORE the first render so the modal never flashes
          // the mount step it is about to skip. Note the index is read from
          // the ACTIVE step list — it is 1 in both, but a hardcoded 1 would
          // silently rot if either array ever gains a step.
          this.state.step = this.getSteps().indexOf('path');
          this.persistState();
          this.updateFabState();
        }
        this.openModal(opts.opener || null);
        if (hasPreset) this.trackStepReached(this.state.step);
      }

      this.trackExternalOpen(opts.source, opts.label);
      return true;
    }

    // Mirrors the FAB's data-track-click='open_will_it_fit' for openers that
    // cannot reach the delegated analytics listener in analytics-tracking.js
    // — it is a bubble-phase document listener, so a stopPropagation'd click
    // never gets there. md_element_click is the ONLY channel the Super App
    // pixel subscribes to; never invent a bespoke event name here.
    trackExternalOpen(source, label) {
      publishElementClick({
        element_action: 'open_will_it_fit',
        element_section: source || 'willitfit_wizard',
        element_label: label || 'Will it fit?',
      });
    }

    openModal(opener) {
      if (this.isOpen || !this.overlayEl) return;
      this.lastOpener = opener || null;

      this.overlayEl.hidden = false;
      var focusTarget = this.renderStep(this.state.step);

      // Force reflow so the transition from the just-removed [hidden] runs.
      void this.overlayEl.offsetWidth;
      this.overlayEl.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      // Hides the PDP sticky bar (z-index 1111) while the wizard is open —
      // the overlay stacks below it so the cart drawer (1000) stays on top.
      document.body.classList.add('willitfit-open');
      this.isOpen = true;

      this.applyFocusTrap(focusTarget);
    }

    closeModal() {
      if (!this.isOpen || !this.overlayEl) return;

      var currentStepName = this.getSteps()[this.state.step];
      if (MEASURE_STEPS.indexOf(currentStepName) !== -1) {
        this.saveMeasurementValueToState(currentStepName);
        this.persistState();
      }

      this.isOpen = false;
      this.overlayEl.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('willitfit-open');
      this.pauseAllVideos();

      var self = this;
      var opener = this.lastOpener;
      var settled = false;

      function finish() {
        if (settled) return;
        settled = true;
        self.overlayEl.removeEventListener('transitionend', onTransitionEnd);
        self.overlayEl.hidden = true;
        self.removeFallbackFocusTrap();
        if (opener && typeof opener.focus === 'function') opener.focus();
      }

      function onTransitionEnd(e) {
        if (e.target !== self.overlayEl) return;
        finish();
      }

      this.overlayEl.addEventListener('transitionend', onTransitionEnd);
      setTimeout(finish, this.getDurationMedium() + 50);
    }

    getDurationMedium() {
      if (this._durationMs) return this._durationMs;
      var ms = 300;
      try {
        var raw = window
          .getComputedStyle(document.documentElement)
          .getPropertyValue('--duration-medium')
          .trim();
        if (raw) {
          if (raw.indexOf('ms') !== -1) ms = parseFloat(raw);
          else if (raw.indexOf('s') !== -1) ms = parseFloat(raw) * 1000;
        }
      } catch (e) {
        // fall through to default
      }
      if (!ms || isNaN(ms) || ms <= 0) ms = 300;
      this._durationMs = ms;
      return ms;
    }

    /* ---------------------------------------------------------------- */
    /* Focus management (self-contained, visibility-aware Tab trap)      */
    /* ---------------------------------------------------------------- */

    applyFocusTrap(focusTarget) {
      var target = focusTarget || this.modalEl;
      if (!target) return;
      // Always use our own visibility-aware trap. The global window.trapFocus
      // computes first/last from ALL focusables in the container WITHOUT
      // filtering hidden ones, so in this multi-step modal its "last" is the
      // permanently-present-but-hidden Done button — the forward-Tab wrap never
      // fires and keyboard focus escapes the open dialog. handleFallbackKeydown
      // recomputes the visible focusables (offsetParent !== null) on every Tab,
      // so it stays correct as steps show/hide their controls.
      this.fallbackFocusTrap(target);
    }

    fallbackFocusTrap(target) {
      if (target && typeof target.focus === 'function') target.focus();
      document.removeEventListener('keydown', this._boundFallbackKeydown);
      document.addEventListener('keydown', this._boundFallbackKeydown);
    }

    removeFallbackFocusTrap() {
      document.removeEventListener('keydown', this._boundFallbackKeydown);
    }

    handleFallbackKeydown(e) {
      if (!this.isOpen || e.key !== 'Tab' || !this.modalEl) return;
      var focusables = this.getFocusableInModal();
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    getFocusableInModal() {
      if (!this.modalEl) return [];
      var nodes = Array.prototype.slice.call(
        this.modalEl.querySelectorAll(
          'button, [href], input, select, textarea, video[controls], [tabindex]:not([tabindex="-1"])'
        )
      );
      return nodes.filter(function (el) {
        return !el.disabled && el.offsetParent !== null;
      });
    }

    /* ---------------------------------------------------------------- */
    /* Step rendering                                                    */
    /* ---------------------------------------------------------------- */

    getWholeEl(measure) {
      return this.querySelector('[data-willitfit-whole="' + measure + '"]');
    }

    getFracEl(measure) {
      return this.querySelector('[data-willitfit-frac="' + measure + '"]');
    }

    // Width/height/jamb each render TWO pre-built video_tag slots (existing +
    // newframe), toggled by this.state.path — key is e.g. 'width-existing'.
    getVideoSlot(key) {
      return this.querySelector('[data-willitfit-video="' + key + '"]');
    }

    getVideoEl(key) {
      var slot = this.getVideoSlot(key);
      return slot ? slot.querySelector('video') : null;
    }

    renderStep(index) {
      var steps = this.getSteps();
      var stepName = steps[index];
      this.stepSections.forEach(function (sec) {
        sec.hidden = sec.getAttribute('data-willitfit-step') !== stepName;
      });

      // Correct on every render regardless of step, since the customer can
      // navigate Back/Forward across it.
      this.updateFramingNote();

      var focusTarget = null;
      if (MEASURE_STEPS.indexOf(stepName) !== -1) {
        focusTarget = this.setupMeasurementStep(stepName);
      } else if (stepName === 'handing') {
        focusTarget = this.setupHandingStep();
      } else if (stepName === 'results') {
        this.renderResults();
        // Reaching the results screen IS the completion signal — the customer
        // sees their numbers here whether or not they ever press Done (many
        // close with X or the overlay). Announced from renderStep rather than
        // renderResults so it fires once per arrival at the step, not on every
        // internal re-render.
        this.announceResult('results');
        // Focus the results heading (tabindex=-1) so the role="status" content
        // is announced from the top, instead of moving focus to a control that
        // pre-empts the live-region announcement of the measurements.
        focusTarget = this.resultsHeading || this.restartBtn || this.closeBtn;
      } else if (stepName === 'mount') {
        this.updateMountSelectionUI();
        focusTarget = this.getSelectedMountButton() || this.mountButtons[0];
      } else if (stepName === 'path') {
        this.updatePathSelectionUI();
        focusTarget = this.getSelectedPathButton() || this.pathButtons[0];
      }

      this.updateProgress(index);
      this.updateFooterVisibility(stepName);
      return focusTarget;
    }

    setupMeasurementStep(measure) {
      var path = this.state.path || 'existing';
      var otherPath = path === 'existing' ? 'newframe' : 'existing';

      // Show + play the slot matching the chosen path; hide + pause its sibling.
      // Both slots are fully pre-rendered by video_tag (real <source> elements
      // already present), so nothing here ever assigns src — the browser just
      // doesn't fetch the paused/hidden one's video bytes (preload='none').
      // Poster IS assigned here (from data-poster, see updateHandingVideoVisibility
      // for the full rationale) since a real `poster` attribute is fetched
      // eagerly by the browser regardless of hidden/preload state.
      var activeSlot = this.getVideoSlot(measure + '-' + path);
      var inactiveSlot = this.getVideoSlot(measure + '-' + otherPath);
      if (activeSlot) {
        activeSlot.hidden = false;
        var activeSlotVideo = activeSlot.querySelector('video');
        if (
          activeSlotVideo &&
          !activeSlotVideo.poster &&
          activeSlotVideo.dataset.poster
        ) {
          activeSlotVideo.poster = activeSlotVideo.dataset.poster;
        }
      }
      if (inactiveSlot) inactiveSlot.hidden = true;

      var inactiveVideo = this.getVideoEl(measure + '-' + otherPath);
      if (inactiveVideo) {
        try {
          inactiveVideo.pause();
        } catch (e) {
          // ignore
        }
      }

      var video = this.getVideoEl(measure + '-' + path);
      if (video) {
        try {
          video.currentTime = 0;
        } catch (e) {
          // restart-from-0 is a nicety, not required
        }
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {
            // Autoplay rejected — no-op, user can press native controls if any.
          });
        }
      }

      this.hintEls.forEach(function (hint) {
        var key = hint.getAttribute('data-willitfit-hint');
        hint.hidden = key !== measure + '-' + path;
      });

      // Path-specific "enter your <RO|door> measurement below" prompt.
      this.promptEls.forEach(function (p) {
        p.hidden = p.getAttribute('data-willitfit-prompt') !== measure + '-' + path;
      });

      // The 'newframe' hint/prompt copy is frozen Liquid markup written for
      // Flush Mount's rough-opening-stud-to-stud measurement. Surface Mount's
      // newframe path asks for a planned Finished Opening instead (see
      // computeDoorAndRO) — overwrite the text on every render so it matches
      // whichever mount is active, in both directions.
      if (path === 'newframe' && (measure === 'width' || measure === 'height')) {
        var mountKey = this.state.mount === 'surface' ? 'surface' : 'flush';
        var copy = NEWFRAME_MEASURE_COPY[mountKey][measure];
        var hintEl = this.hintEls.filter(function (h) {
          return h.getAttribute('data-willitfit-hint') === measure + '-newframe';
        })[0];
        var promptEl = this.promptEls.filter(function (p) {
          return p.getAttribute('data-willitfit-prompt') === measure + '-newframe';
        })[0];
        if (hintEl) hintEl.textContent = copy.hint;
        if (promptEl) promptEl.textContent = copy.prompt;
      }

      // Jamb is a fixed two-option choice (4 9/16" / 6 9/16"), not a free
      // input. Surface Mount never reaches this step (see getSteps()).
      if (measure === 'jamb') {
        this.updateJambSelectionUI();
        this.updateNextButtonState('jamb');
        var selectedJamb = this.getSelectedJambButton();
        return selectedJamb || (this.jambButtons.length ? this.jambButtons[0] : null);
      }

      var wholeEl = this.getWholeEl(measure);
      var fracEl = this.getFracEl(measure);
      var stored = this.state.values[measure] || { whole: '', frac: '0/16' };
      if (wholeEl) wholeEl.value = stored.whole || '';
      if (fracEl) fracEl.value = stored.frac || '0/16';

      this.hideError(measure);
      this.updateNextButtonState(measure);
      this.updateSoftNote(measure);

      return wholeEl;
    }

    leaveStep(index) {
      var name = this.getSteps()[index];
      if (name === 'handing') {
        this.pauseHandingVideos();
        return;
      }
      if (MEASURE_STEPS.indexOf(name) === -1) return;
      var self = this;
      ['existing', 'newframe'].forEach(function (path) {
        var v = self.getVideoEl(name + '-' + path);
        if (v) {
          try {
            v.pause();
            // .load() releases the decoder session (see pauseHandingVideos'
            // comment) — without it, a measurement video left merely paused
            // still holds a decoder slot when the handing step's own
            // staggered-autoplay mitigation runs later, undermining the
            // reason that stagger exists on iOS's small decoder pool.
            v.load();
          } catch (e) {
            // ignore
          }
        }
      });
    }

    pauseAllVideos() {
      var self = this;
      MEASURE_STEPS.forEach(function (m) {
        ['existing', 'newframe'].forEach(function (path) {
          var v = self.getVideoEl(m + '-' + path);
          if (v) {
            try {
              v.pause();
              v.load();
            } catch (e) {
              // ignore
            }
          }
        });
      });
      this.pauseHandingVideos();
    }

    updateProgress(index) {
      var totalSteps = this.getSteps().length - 1; // 'results' isn't a numbered step
      var pct = (Math.min(index, totalSteps) / totalSteps) * 100;
      if (this.progressFill) this.progressFill.style.width = pct + '%';
      if (this.progressText) {
        this.progressText.textContent =
          index < totalSteps
            ? 'Step ' + (index + 1) + ' of ' + totalSteps
            : 'Complete';
      }
      if (this.progressTrack) {
        // aria-valuemax is mount-dependent (STEPS_FLUSH=6, STEPS_SURFACE=5)
        // and the Liquid-rendered default is a static '5' - set it here too,
        // not just aria-valuenow, or a Flush Mount session's valuenow (6 at
        // the handing step) exceeds a stale valuemax, an ARIA spec violation.
        this.progressTrack.setAttribute('aria-valuemax', String(totalSteps));
        this.progressTrack.setAttribute(
          'aria-valuenow',
          String(Math.min(index + 1, totalSteps))
        );
      }
    }

    updateFooterVisibility(stepName) {
      // 'mount' is the true first step (nothing to go Back to) so its footer
      // is hidden entirely, same as the old single combined step. 'path' now
      // sits at index 1 — it needs a visible Back button (to return to
      // mount) even though, like mount, its own choice buttons still apply
      // immediately (no explicit Next click), so Next stays hidden there too.
      var isMount = stepName === 'mount';
      var isPath = stepName === 'path';
      var isResults = stepName === 'results';
      if (this.footerEl) this.footerEl.hidden = isMount;
      if (this.backBtn) this.backBtn.hidden = isMount;
      if (this.nextBtn) this.nextBtn.hidden = isMount || isPath || isResults;
      if (this.doneBtn) this.doneBtn.hidden = !isResults;
      // "Start over" now lives inside the results section, shown with it.
    }

    // Surface Mount + "newly framed opening": the unit overlays a FINISHED
    // wall, not exposed studs/drywall — advise the customer before they
    // measure (width step) and again right before they act (results step).
    updateFramingNote() {
      var show = this.state.mount === 'surface' && this.state.path === 'newframe';
      this.framingNoteEls.forEach(function (el) {
        el.hidden = !show;
      });
    }

    goToStep(newIndex) {
      this.leaveStep(this.state.step);
      this.state.step = newIndex;
      this.persistState();
      var focusTarget = this.renderStep(newIndex);
      this.updateFabState();
      if (this.isOpen) this.applyFocusTrap(focusTarget);
      this.trackStepReached(newIndex);
    }

    // Funnel visibility for every step transition (forward AND back-navigation).
    // Fired unconditionally, undeduped — funnel analysis dedupes by
    // sessionId+step in SQL later. Never throws: a tracking failure must never
    // break the wizard.
    trackStepReached(index) {
      var stepName = (this.getSteps() || [])[index];
      publishElementClick({
        element_action: 'willitfit_step_' + stepName,
        element_section: 'willitfit_wizard',
        element_label: stepName,
      });
    }

    /* ---------------------------------------------------------------- */
    /* Validation                                                        */
    /* ---------------------------------------------------------------- */

    isValidMeasurement(measure) {
      if (measure === 'handing') {
        return HANDING_VALUES.indexOf(this.state.values.handing) !== -1;
      }
      if (measure === 'jamb') {
        var j = this.state.values.jamb || { whole: '' };
        return j.whole === '4' || j.whole === '6';
      }
      var wholeEl = this.getWholeEl(measure);
      if (!wholeEl) return false;
      var wholeStr = wholeEl.value || '';
      if (!WHOLE_REGEX.test(wholeStr)) return false;
      var wholeNum = parseInt(wholeStr, 10);
      if (isNaN(wholeNum) || wholeNum < 0 || wholeNum > 199) return false;

      var fracEl = this.getFracEl(measure);
      var fracStr = fracEl ? fracEl.value || '0/16' : '0/16';
      var sixteenths = parseInt(fracStr.split('/')[0], 10) || 0;

      return wholeNum > 0 || sixteenths > 0;
    }

    showError(measure) {
      var errEl = this.errorEls[measure];
      if (errEl) errEl.hidden = false;
      var wholeEl = this.getWholeEl(measure);
      if (wholeEl) {
        wholeEl.classList.add('willitfit__whole--error');
        wholeEl.setAttribute('aria-invalid', 'true');
        wholeEl.focus();
      }
    }

    hideError(measure) {
      var errEl = this.errorEls[measure];
      if (errEl) errEl.hidden = true;
      var wholeEl = this.getWholeEl(measure);
      if (wholeEl) {
        wholeEl.classList.remove('willitfit__whole--error');
        wholeEl.removeAttribute('aria-invalid');
      }
    }

    updateNextButtonState(measure) {
      if (measure !== this.getSteps()[this.state.step]) return;
      if (this.nextBtn) this.nextBtn.disabled = !this.isValidMeasurement(measure);
    }

    isWithinProductRange(measure) {
      // Surface Mount's range doesn't depend on path (see computeDoorAndRO —
      // the entered value is the Finished Opening either way), so it's keyed
      // by measure only.
      var range;
      if (this.state.mount === 'surface') {
        range = SURFACE_RANGES[measure];
      } else {
        var path = this.state.path;
        range = path && RANGES[path] && RANGES[path][measure];
      }
      if (!range) return true;
      var wholeEl = this.getWholeEl(measure);
      if (!wholeEl) return true;
      var whole = parseInt(wholeEl.value, 10);
      if (isNaN(whole)) return true; // empties are the hard validator's job
      var fracEl = this.getFracEl(measure);
      var six = fracEl ? parseInt((fracEl.value || '0/16').split('/')[0], 10) || 0 : 0;
      var total = whole * 16 + six;
      return total >= range[0] * 16 && total <= range[1] * 16;
    }

    // Advisory only — does NOT block Next. Shown when the entry is otherwise
    // valid but outside our standard door sizes.
    updateSoftNote(measure) {
      var note = this.softNoteEls[measure];
      if (!note) return;
      note.hidden = !(
        this.isValidMeasurement(measure) && !this.isWithinProductRange(measure)
      );
    }

    saveMeasurementValueToState(measure) {
      if (measure === 'jamb' || measure === 'handing') return; // saved on choice selection
      var wholeEl = this.getWholeEl(measure);
      var fracEl = this.getFracEl(measure);
      this.state.values[measure] = {
        whole: wholeEl ? wholeEl.value || '' : '',
        frac: fracEl ? fracEl.value || '0/16' : '0/16',
      };
    }

    /* ---------------------------------------------------------------- */
    /* Results / RO math                                                 */
    /* ---------------------------------------------------------------- */

    // Computes the "effective" width/height the customer's measurement
    // implies. Mount-aware, NOT just path-aware — Flush Mount and Surface
    // Mount need different conversions because they install differently:
    //
    // Flush Mount installs a pre-hung door unit (jamb + slab) INTO a framed
    // Rough Opening, with a fixed, universal ~2" shim/jamb tolerance between
    // RO and the door unit itself. So for Flush Mount: existing path ->
    // entered value IS the door size; newframe path -> entered value is the
    // RO, door = RO - 2".
    //
    // Surface Mount sits ON TOP of a finished wall surface — there is no
    // jamb-in-rough-opening fit, and no catalog SKU (single door or French
    // door) is ever sized by Rough Opening, only by Finished Opening (see
    // SURFACE_SIZE_TIERS / the surface-mounted-french-door variant titles,
    // e.g. "For 48" x 80" Finished Opening"). For an existing door, that
    // opening's jamb IS already the Finished Opening — no conversion. For a
    // newly framed opening, the customer hasn't finished the wall yet (per
    // the framing-note copy, drywall/trim are still pending) and is
    // reporting their PLANNED Finished Opening target, not a stud-to-stud
    // measurement that needs a fixed manufacturing offset applied — Surface
    // Mount has no jamb/shim convention to convert one into the other. So for
    // Surface Mount, the entered value is used as-is in BOTH paths.
    computeDoorAndRO(measure) {
      var stored = this.state.values[measure] || { whole: '0', frac: '0/16' };
      var enteredWhole = parseInt(stored.whole, 10);
      if (isNaN(enteredWhole)) enteredWhole = 0;
      var sixteenths = parseInt((stored.frac || '0/16').split('/')[0], 10) || 0;

      // Doors are sold in whole-inch widths/heights only. Recommend the largest
      // whole-inch door that still fits (floor) so it is never too tight; the
      // rough opening is always that door + 2" for Flush Mount. Work in total
      // sixteenths so the 2" (=32/16) offset is applied before flooring.
      var enteredTotal = enteredWhole * 16 + sixteenths;
      var doorInches;
      if (this.state.mount === 'surface' || this.state.path === 'existing') {
        // Surface Mount (either path): entered value is the Finished Opening.
        // Flush Mount existing path: entered value is the existing door size.
        doorInches = Math.floor(enteredTotal / 16);
      } else {
        // Flush Mount newframe path only: entered value is the rough
        // opening; door = RO - 2".
        doorInches = Math.max(0, Math.floor((enteredTotal - 32) / 16));
      }
      return {
        door: { whole: doorInches, sixteenths: 0 },
        ro: { whole: doorInches + 2, sixteenths: 0 },
      };
    }

    // Renders a measurement output as a sequence of segments so we never
    // need innerHTML: plain segments become text nodes, {mono:true}
    // segments become <span class="willitfit__num"> wrappers.
    setOutput(measure, segments) {
      var el = this.outEls[measure];
      if (!el) return;
      while (el.firstChild) el.removeChild(el.firstChild);
      segments.forEach(function (seg) {
        if (seg.mono) {
          var span = document.createElement('span');
          span.className = 'willitfit__num';
          span.textContent = seg.text;
          el.appendChild(span);
        } else {
          el.appendChild(document.createTextNode(seg.text));
        }
      });
    }

    /**
     * PUBLIC READ API — the customer's finished measurements, normalised for
     * consumers outside this widget (today: the configurator's prefill module,
     * assets/md-config-support-willitfit-prefill.js).
     *
     * Returns the DOOR size, not the raw number the customer typed. The whole
     * point of the wizard is the conversion between what you can measure (an
     * existing door, a rough opening, a planned finished opening) and what you
     * order, and that conversion is mount- AND path-dependent — see
     * computeDoorAndRO. Handing the raw entry to a consumer would put that math
     * in two places, which is exactly how the Surface Mount RO bug happened.
     *
     * 🚨 MOUNT-GATED, and this gate is the whole safety story. A Surface Mount
     * result's width/height are Finished Opening sizes for a different product
     * line (tier-matched via SURFACE_SIZE_TIERS), NOT Flush Mount door inches;
     * feeding them into a Flush Mount picker silently orders the wrong door.
     * Surface Mount has no jamb either. So when the page declares its own mount
     * (data-willitfit-page-mount, from product.tags) and the saved result is for
     * the OTHER mount, this returns null rather than something a consumer has to
     * remember to check. `mount` is still reported for consumers that care.
     *
     * Pages with no mount tag (a collection, the homepage) declare nothing and
     * are not gated — there is no product to disagree with.
     *
     * @returns {Object|null} null when there is no complete result that applies
     *   to this page
     */
    getConfiguratorSelections() {
      if (!this._connected || !this.isComplete()) return null;
      if (this.pageMount && this.state.mount !== this.pageMount) return null;

      var widthCalc = this.computeDoorAndRO('width');
      var heightCalc = this.computeDoorAndRO('height');
      var isSurface = this.state.mount === 'surface';
      var jamb = this.state.values.jamb || {};
      var jambWhole = parseInt(jamb.whole, 10);
      var handingKey = this.state.values.handing || null;
      var handingMeta = HANDING_META[handingKey] || null;

      return {
        mount: this.state.mount,
        path: this.state.path,
        doorWidthInches: widthCalc.door.whole,
        doorHeightInches: heightCalc.door.whole,
        roughOpeningWidthInches: widthCalc.ro.whole,
        roughOpeningHeightInches: heightCalc.ro.whole,
        // Surface Mount never reaches the jamb step (see getSteps()), so any
        // jamb value still sitting in state is a leftover from a previous
        // Flush Mount pass — report nothing rather than something stale.
        jambWholeInches: isSurface || isNaN(jambWhole) ? null : jambWhole,
        jambFraction: isSurface ? null : jamb.frac || null,
        // null for a legacy pre-handing-step session (see legacyHandingExempt):
        // isComplete() forgives the missing answer, but there is no answer to
        // report and a consumer must not invent one.
        handing: handingKey,
        hinge: handingMeta ? handingMeta.hinge : null, // 'Left' | 'Right'
        swing: handingMeta ? handingMeta.swing : null, // 'In-Swing' | 'Out-Swing'
      };
    }

    /**
     * Broadcast a finished result to anything on the page that can act on it.
     *
     * A DOM CustomEvent rather than a direct call, mirroring the
     * configurator -> cart-drawer `configurator:cart-add-complete` hand-off:
     * the wizard is global and must not know which page it happens to be on.
     * No listener is the normal case (most templates have no configurator).
     *
     * @param {string} source what prompted it — 'results' or 'done'
     */
    announceResult(source) {
      try {
        var result = this.getConfiguratorSelections();
        if (!result) return;
        document.dispatchEvent(
          new CustomEvent('willitfit:result', {
            detail: { result: result, source: source || 'results' },
          })
        );
      } catch (e) {
        /* silent — a listener throwing must never break the wizard */
      }
    }

    renderResults() {
      var mount = this.state.mount || 'flush';

      if (this.jambRowEl) this.jambRowEl.hidden = mount === 'surface';

      if (mount === 'surface') {
        this.renderSurfaceResults();
      } else {
        this.renderFlushResults();
      }

      var handingMeta = HANDING_META[this.state.values.handing];
      if (handingMeta) {
        this.setOutput('handing', [
          { text: handingMeta.label, mono: true },
          { text: ' (Hinges On: ', mono: false },
          { text: handingMeta.hinge, mono: true },
          { text: ', Swing Direction: ', mono: false },
          { text: handingMeta.swing, mono: true },
          { text: ')', mono: false },
        ]);
      }

      this.updateShopLinks();
    }

    renderFlushResults() {
      var widthCalc = this.computeDoorAndRO('width');
      var heightCalc = this.computeDoorAndRO('height');

      var jambStored = this.state.values.jamb || { whole: '0', frac: '0/16' };
      var jambWhole = parseInt(jambStored.whole, 10);
      if (isNaN(jambWhole)) jambWhole = 0;
      var jambSixteenths = parseInt((jambStored.frac || '0/16').split('/')[0], 10) || 0;

      var widthDoorStr = formatMeasurement(widthCalc.door.whole, widthCalc.door.sixteenths);
      var widthRoStr = formatMeasurement(widthCalc.ro.whole, widthCalc.ro.sixteenths);
      var heightDoorStr = formatMeasurement(heightCalc.door.whole, heightCalc.door.sixteenths);
      var heightRoStr = formatMeasurement(heightCalc.ro.whole, heightCalc.ro.sixteenths);
      var jambStr = formatMeasurement(jambWhole, jambSixteenths);

      this.setOutput('width', [
        { text: widthDoorStr, mono: true },
        { text: ' (Rough Opening ', mono: false },
        { text: widthRoStr, mono: true },
        { text: ')', mono: false },
      ]);
      this.setOutput('height', [
        { text: heightDoorStr, mono: true },
        { text: ' (Rough Opening ', mono: false },
        { text: heightRoStr, mono: true },
        { text: ')', mono: false },
      ]);
      this.setOutput('jamb', [{ text: jambStr, mono: true }]);
    }

    // Surface Mount has no continuous door width — it's a handful of catalog
    // tiers keyed to Finished Opening width (unit = FO + 8"), height fixed at
    // 89-1/8" for an 80" FO. Round the customer's effective FO width UP to
    // the nearest tier (never undersize); fall back to "call us" when it's
    // wider than the largest tier or the height isn't ~80".
    renderSurfaceResults() {
      var widthCalc = this.computeDoorAndRO('width');
      var tier = findSurfaceSizeTier(widthCalc.door.whole);

      if (tier) {
        this.setOutput('width', [
          { text: tier.unitWidth + '" x ' + SURFACE_UNIT_HEIGHT_STR, mono: true },
          {
            text: ' (For ' + tier.foWidth + '" x ' + SURFACE_FO_HEIGHT + '" Finished Opening)',
            mono: false,
          },
        ]);
      } else {
        this.setOutput('width', [
          { text: 'Custom size needed', mono: false },
          { text: ' (call 801-436-5918)', mono: false },
        ]);
      }

      var heightCalc = this.computeDoorAndRO('height');
      var heightOk =
        Math.abs(heightCalc.door.whole - SURFACE_FO_HEIGHT) <= SURFACE_FO_HEIGHT_TOLERANCE;
      if (heightOk) {
        this.setOutput('height', [
          { text: SURFACE_FO_HEIGHT + '" Finished Opening (standard)', mono: true },
        ]);
      } else {
        this.setOutput('height', [
          { text: 'Non-standard height', mono: false },
          { text: ' (call 801-436-5918 for options)', mono: false },
        ]);
      }
    }

    // Show only the shop-link matching the chosen mount type.
    updateShopLinks() {
      var mount = this.state.mount || 'flush';
      this.shopLinkEls.forEach(function (el) {
        el.hidden = el.getAttribute('data-willitfit-shoplink') !== mount;
      });
    }

    pushCompletionAnalytics() {
      // GTM/GA4 lane: independent of the pixel bridge below. A GA4 goal may be
      // wired to this bespoke event NAME, so it is deliberately NOT folded into
      // publishElementClick — do not remove it, and do not rename it. Its own
      // try/catch closes a real gap: this method was the one publish site in
      // the file with no guard at all.
      try {
        // Staff traffic is never published (snippets/md-internal-traffic.liquid).
        if (
          window.MD_INTERNAL_TRAFFIC !== true &&
          window.dataLayer &&
          typeof window.dataLayer.push === 'function'
        ) {
          window.dataLayer.push({ event: 'will_it_fit_complete' });
        }
      } catch (e) {
        /* silent — analytics must never block UX */
      }
      // md_element_click is the ONLY channel the Super App pixel subscribes
      // to — a bespoke Shopify.analytics.publish('will_it_fit_complete', {})
      // call used to sit here and reach nothing; do not reintroduce it.
      publishElementClick({
        element_action: 'willitfit_complete',
        element_section: 'willitfit_wizard',
        element_label: 'results',
      });
    }

    // All measurements captured -> the wizard has a saved result. Jamb is
    // only required for Flush Mount (Surface Mount has no jamb step).
    isComplete() {
      if (!this.state.path || !this.state.mount) return false;
      var v = this.state.values || {};
      var jambOk =
        this.state.mount === 'surface'
          ? true
          : v.jamb && (v.jamb.whole === '4' || v.jamb.whole === '6');
      // Pre-PR saves predate the handing step entirely (not just `mount`) —
      // a genuine legacy session can never have a `handing` value. Without
      // this exemption, loadState()'s mount-defaulting fix alone still isn't
      // enough: a returning customer's "My Door Size" badge would still
      // silently disappear on handingOk alone. legacyHandingExempt is set by
      // loadState() only when mount was missing AND width/height/jamb were
      // already complete (see there for detection + why it must be sticky).
      var handingOk =
        HANDING_VALUES.indexOf(v.handing) !== -1 || !!this.state.legacyHandingExempt;
      return isValidMeasurementValue(v.width) && isValidMeasurementValue(v.height) && jambOk && handingOk;
    }

    // Assign this visitor's copy-test arm exactly once, then hold it.
    // Called immediately after loadState() and BEFORE the first
    // updateFabState(), so no arm can render and then be overwritten.
    assignFabLabelArm() {
      try {
        // Desktop only — the label is display:none below 750px, so a mobile
        // visitor never sees it. Gate on the MEDIA QUERY, never on element
        // presence: the node exists at every width, and counting it would
        // make roughly 70% of the denominator phantom.
        if (
          typeof window.matchMedia !== 'function' ||
          !window.matchMedia('(min-width: 750px)').matches
        ) {
          return;
        }

        // The completed "My Door Size" receipt owns the label outright.
        if (this.isComplete()) return;

        var arm = null;
        try {
          arm = findFabArm(window.localStorage.getItem(FAB_LABEL_KEY));
          if (!arm) {
            arm =
              FAB_LABEL_VARIANTS[
                Math.floor(Math.random() * FAB_LABEL_VARIANTS.length)
              ];
            window.localStorage.setItem(FAB_LABEL_KEY, arm.slug);
          }
        } catch (e) {
          // Private mode / quota / cookie blockers. Do NOT re-roll per
          // pageview — that would spray one person across arms. Serve control
          // copy under a distinct, excluded slug instead.
          arm = null;
        }

        var slug = arm ? arm.slug : FAB_LABEL_FALLBACK_SLUG;
        var copy = arm ? arm.copy : FAB_LABEL_CONTROL_COPY;

        // ── ONE SYNCHRONOUS TURN from here to the exposure publish ──
        // No await, no setTimeout, no rAF between the dataset write and the
        // publish. Because JS runs to completion, a click can only observe a
        // real arm slug in data-track-label AFTER this block has already
        // fired its matching exposure row. A yield point here reopens a
        // window where clicks orphan from their denominator.
        this._fabAssignedLabel = copy;
        this._fabAssignedAria = copy + FAB_LABEL_ARIA_SUFFIX;
        this._fabAssignedSlug = slug;
        if (this.fabButton) this.fabButton.dataset.trackLabel = slug;

        if (!document[FAB_EXPOSURE_FLAG]) {
          document[FAB_EXPOSURE_FLAG] = true;
          publishSectionVisible({
            element_section: 'willitfit_wizard',
            element_label: slug,
          });
        }
      } catch (e) {
        /* silent — a copy test must never break the wizard */
      }
    }

    // Morph the omnipresent button into a "saved result" receipt.
    updateFabState() {
      var done = this.isComplete();
      // BOTH ternaries must source the incomplete-state copy from the
      // assigned arm. Patching only textContent ships a button that reads
      // "Find my size" while announcing "Will it fit?" — an SC 2.5.3
      // Label-in-Name violation on 9 of 10 arms.
      if (this.fabLabel) {
        this.fabLabel.textContent = done
          ? 'My Door Size'
          : this._fabAssignedLabel || FAB_LABEL_CONTROL_COPY;
      }
      if (this.fabBadge) this.fabBadge.hidden = !done;
      // data-track-label drives the experiment's NUMERATOR, so it needs the
      // same done/not-done treatment as the text and aria above. Restoring the
      // arm on the not-done branch matters: "Start over" flips isComplete back
      // to false, and leaving the receipt slug there would mis-tag a visitor
      // who is back in the funnel. Never write when no arm was assigned (mobile
      // or already-complete-at-load) — the seeded sentinel must survive.
      if (this.fabButton) {
        if (done) {
          this.fabButton.dataset.trackLabel = FAB_LABEL_DONE_SLUG;
        } else if (this._fabAssignedSlug) {
          this.fabButton.dataset.trackLabel = this._fabAssignedSlug;
        }
      }
      if (this.fabButton) {
        this.fabButton.setAttribute(
          'aria-label',
          done
            ? 'My Door Size - view your saved measurements'
            : this._fabAssignedAria ||
                FAB_LABEL_CONTROL_COPY + FAB_LABEL_ARIA_SUFFIX
        );
      }
    }

    /* ---------------------------------------------------------------- */
    /* User actions                                                      */
    /* ---------------------------------------------------------------- */

    onChooseMount(mount) {
      if (mount !== 'flush' && mount !== 'surface') return;
      this.state.mount = mount;
      // A previously-saved In-Swing handing answer is invalid once the
      // customer (re)selects Surface Mount — clear it so isComplete()/the
      // handing step don't carry a selection that's no longer offered.
      if (mount === 'surface' && OUTSWING_VALUES.indexOf(this.state.values.handing) === -1) {
        this.state.values.handing = '';
      }
      // Actively re-choosing a mount means this is a fresh pass through the
      // wizard, not just reopening an old legacy badge — the handing-step
      // exemption from loadState() must not carry forward and let a new,
      // still-unanswered handing step read as already complete.
      this.state.legacyHandingExempt = false;
      this.goToStep(1); // -> path
    }

    onChoosePath(path) {
      if (path !== 'existing' && path !== 'newframe') return;
      this.state.path = path;
      this.goToStep(2); // -> width (index 2 in both STEPS_FLUSH and STEPS_SURFACE)
    }

    onSelectJamb(btn) {
      this.state.values.jamb = {
        whole: btn.getAttribute('data-jamb-whole'),
        frac: btn.getAttribute('data-jamb-frac'),
      };
      this.persistState();
      this.updateJambSelectionUI();
      this.updateNextButtonState('jamb');
    }

    getSelectedMountButton() {
      var mount = this.state.mount;
      for (var i = 0; i < this.mountButtons.length; i++) {
        if (this.mountButtons[i].getAttribute('data-willitfit-mount') === mount) {
          return this.mountButtons[i];
        }
      }
      return null;
    }

    getSelectedPathButton() {
      var path = this.state.path;
      for (var i = 0; i < this.pathButtons.length; i++) {
        if (this.pathButtons[i].getAttribute('data-willitfit-path') === path) {
          return this.pathButtons[i];
        }
      }
      return null;
    }

    getSelectedJambButton() {
      var jamb = this.state.values.jamb || {};
      for (var i = 0; i < this.jambButtons.length; i++) {
        if (this.jambButtons[i].getAttribute('data-jamb-whole') === jamb.whole) {
          return this.jambButtons[i];
        }
      }
      return null;
    }

    updateJambSelectionUI() {
      var jamb = this.state.values.jamb || {};
      this.jambButtons.forEach(function (btn) {
        var isSel = btn.getAttribute('data-jamb-whole') === jamb.whole;
        btn.classList.toggle('is-selected', isSel);
        btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
      });
    }

    onSelectHanding(btn) {
      var value = btn.getAttribute('data-handing-value');
      if (HANDING_VALUES.indexOf(value) === -1) return;
      // In-Swing tiles are disabled for Surface Mount — belt-and-suspenders
      // guard in case a disabled tile's click still reaches here.
      if (this.state.mount === 'surface' && OUTSWING_VALUES.indexOf(value) === -1) return;
      this.state.values.handing = value;
      this.persistState();
      this.updateHandingSelectionUI();
      this.updateNextButtonState('handing');
    }

    getSelectedHandingButton() {
      var value = this.state.values.handing;
      for (var i = 0; i < this.handingButtons.length; i++) {
        if (this.handingButtons[i].getAttribute('data-handing-value') === value) {
          return this.handingButtons[i];
        }
      }
      return null;
    }

    updateHandingSelectionUI() {
      var value = this.state.values.handing;
      this.handingButtons.forEach(function (btn) {
        var isSel = btn.getAttribute('data-handing-value') === value;
        btn.classList.toggle('is-selected', isSel);
        btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
      });
    }

    // Surface Mount has no in-swing hardware — hide those tiles entirely
    // (not just disable them) so the grid only ever shows real options for
    // the chosen mount type, and show the explanatory note above the grid
    // so it's clear why only 2 tiles appear instead of 4.
    updateOutSwingAvailability() {
      var outswingOnly = this.state.mount === 'surface';
      if (this.surfaceOutswingNoteEl) this.surfaceOutswingNoteEl.hidden = !outswingOnly;
      this.handingButtons.forEach(function (btn) {
        var isInSwing = btn.hasAttribute('data-outswing-only');
        btn.hidden = outswingOnly && isInSwing;
      });
    }

    // Each handing direction renders BOTH a flush-mount and a surface-mount
    // preview slot; show + play only the one matching the current mount
    // (mirrors setupMeasurementStep's existing/newframe slot toggle).
    //
    // Poster images are NOT set server-side (Liquid emits them as
    // data-poster, not poster — see snippets/willitfit.liquid) because
    // <video poster> is fetched eagerly regardless of preload/hidden state,
    // which was pulling ~200-400KB of thumbnail images on every single
    // pageview sitewide (this widget renders unconditionally in
    // theme.liquid) even though almost no visitor ever opens the handing
    // step. Assigning `.poster` here means it only fetches once a visitor
    // actually reaches this step, and only for the mount variant they're on.
    updateHandingVideoVisibility() {
      var mount = this.state.mount === 'surface' ? 'surface' : 'flush';
      var otherMount = mount === 'surface' ? 'flush' : 'surface';
      var self = this;
      HANDING_VALUES.forEach(function (direction) {
        var activeSlot = self.querySelector(
          '[data-willitfit-handing-video="' + direction + '-' + mount + '"]'
        );
        var inactiveSlot = self.querySelector(
          '[data-willitfit-handing-video="' + direction + '-' + otherMount + '"]'
        );
        if (activeSlot) {
          activeSlot.hidden = false;
          var activeVideo = activeSlot.querySelector('video');
          if (activeVideo && !activeVideo.poster && activeVideo.dataset.poster) {
            activeVideo.poster = activeVideo.dataset.poster;
          }
        }
        if (inactiveSlot) inactiveSlot.hidden = true;
        var inactiveVideo = inactiveSlot ? inactiveSlot.querySelector('video') : null;
        if (inactiveVideo) {
          try {
            inactiveVideo.pause();
          } catch (e) {
            // ignore
          }
        }
      });
    }

    // Only the ACTIVE (visible, mount-matched) slot's <video> per direction —
    // and only when its parent tile isn't hidden. Surface Mount hides the
    // two In-Swing tiles via updateOutSwingAvailability() (button.hidden),
    // independently of the flush/surface slot toggle here, so without this
    // check a Surface Mount handing-step would still hydrate + autoplay the
    // 2 invisible In-Swing videos underneath their hidden buttons.
    getHandingVideos() {
      var mount = this.state.mount === 'surface' ? 'surface' : 'flush';
      var self = this;
      return HANDING_VALUES.map(function (direction) {
        var slot = self.querySelector(
          '[data-willitfit-handing-video="' + direction + '-' + mount + '"]'
        );
        if (!slot) return null;
        var btn = slot.closest('[data-willitfit-handing-choice]');
        if (btn && btn.hidden) return null;
        return slot.querySelector('video');
      }).filter(Boolean);
    }

    // Plays the active-mount preview video for each handing direction once
    // the step is reached. Each <video> is fully pre-rendered by Liquid's
    // video_tag (real <source> elements + Shopify's own auto-generated
    // poster already present), so this never assigns src/poster —
    // preload='none' means nothing streams until .play() is actually called
    // here. muted/loop/playsinline (set in Liquid) satisfy iOS/macOS
    // Safari's autoplay policy; a rejected play() just no-ops, since the
    // poster frame already shows a static fallback.
    // Staggered (not simultaneous) — iOS WebKit's hardware video decoder pool
    // is small (tighter on A9-A11 chips: iPhone 6s/7/8/SE1), and 4 concurrent
    // autoplay .play() calls risk silent decoder-pool exhaustion on those
    // devices. ~120ms apart keeps each decode request off the same tick
    // without a visually noticeable delay.
    hydrateAndPlayHandingVideos() {
      this.clearPendingHandingPlays();
      var self = this;
      this.getHandingVideos().forEach(function (video, i) {
        var timeoutId = window.setTimeout(function () {
          var playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(function () {
              // Autoplay rejected — no-op.
            });
          }
        }, i * 120);
        self._handingPlayTimeouts.push(timeoutId);
      });
    }

    // Cancels any staggered plays not yet fired — a customer can leave the
    // handing step (Back/mount switch) before all 4 timeouts elapse.
    clearPendingHandingPlays() {
      this._handingPlayTimeouts.forEach(function (id) {
        window.clearTimeout(id);
      });
      this._handingPlayTimeouts = [];
    }

    // Pauses BOTH mount variants' videos for every direction — a customer
    // may have switched mount type since a slot last played.
    pauseHandingVideos() {
      this.clearPendingHandingPlays();
      var self = this;
      HANDING_VALUES.forEach(function (direction) {
        ['flush', 'surface'].forEach(function (mount) {
          var slot = self.querySelector(
            '[data-willitfit-handing-video="' + direction + '-' + mount + '"]'
          );
          var video = slot ? slot.querySelector('video') : null;
          if (video) {
            try {
              video.pause();
              // .load() resets the media element back to its initial state,
              // which re-displays the `poster` frame instead of leaving a
              // frozen, potentially mid-swing video frame on screen — a
              // stopped-looking pause reads as intentional, a frozen video
              // frame reads as broken. Safe with preload='none': per spec,
              // load() doesn't eagerly fetch source bytes, it just resets
              // readyState (and, as a side effect, releases the decoder
              // session — a nice-to-have on top of the decoder-pressure
              // mitigation elsewhere in this file).
              video.load();
            } catch (e) {
              // ignore
            }
          }
        });
      });
    }

    // Flips the paused/playing state for all 4 handing-preview videos and
    // updates the toggle button's accessible name/state. This is the WCAG
    // 2.2.2 pause mechanism for the autoplaying, looping tile previews.
    togglePreviews() {
      this.previewsPaused = !this.previewsPaused;
      if (this.previewsPaused) {
        this.pauseHandingVideos();
      } else {
        this.hydrateAndPlayHandingVideos();
      }
      this.updatePausePreviewsUI();
    }

    updatePausePreviewsUI() {
      if (!this.pausePreviewsBtn) return;
      this.pausePreviewsBtn.setAttribute(
        'aria-pressed',
        this.previewsPaused ? 'true' : 'false'
      );
      this.pausePreviewsBtn.textContent = this.previewsPaused
        ? 'Play previews'
        : 'Pause previews';
    }

    setupHandingStep() {
      this.updateOutSwingAvailability();
      this.updateHandingVideoVisibility();
      this.updateHandingSelectionUI();
      this.updateNextButtonState('handing');
      this.updatePausePreviewsUI();
      if (this.previewsPaused) {
        this.pauseHandingVideos();
      } else {
        this.hydrateAndPlayHandingVideos();
      }

      var selectedBtn = this.getSelectedHandingButton();
      if (selectedBtn) return selectedBtn;
      // updateOutSwingAvailability() above may have just hidden the In-Swing
      // tiles for Surface Mount — handingButtons[0] is always "Right-Hand
      // In-Swing" in DOM order, so falling back to it unfiltered would try to
      // focus a hidden element (a no-op per spec, silently stranding focus).
      var firstVisible = this.handingButtons.filter(function (btn) {
        return !btn.hidden;
      })[0];
      return firstVisible || null;
    }

    updateMountSelectionUI() {
      var mount = this.state.mount;
      this.mountButtons.forEach(function (btn) {
        var isSel = btn.getAttribute('data-willitfit-mount') === mount;
        btn.classList.toggle('is-selected', isSel);
        btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
      });
    }

    updatePathSelectionUI() {
      var path = this.state.path;
      this.pathButtons.forEach(function (btn) {
        var isSel = btn.getAttribute('data-willitfit-path') === path;
        btn.classList.toggle('is-selected', isSel);
        btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
      });
    }

    onNext() {
      var stepName = this.getSteps()[this.state.step];
      if (MEASURE_STEPS.indexOf(stepName) === -1 && stepName !== 'handing') return;

      if (!this.isValidMeasurement(stepName)) {
        this.showError(stepName);
        return;
      }
      this.hideError(stepName);
      this.saveMeasurementValueToState(stepName);

      var wasFinalStep = stepName === 'handing';
      this.goToStep(this.state.step + 1);
      if (wasFinalStep) this.pushCompletionAnalytics();
    }

    onBack() {
      if (this.state.step <= 0) return;
      var stepName = this.getSteps()[this.state.step];
      if (MEASURE_STEPS.indexOf(stepName) !== -1) {
        this.saveMeasurementValueToState(stepName);
      }
      this.goToStep(this.state.step - 1);
    }

    onRestart() {
      this.leaveStep(this.state.step);
      this.state = { path: null, mount: null, step: 0, values: defaultValues() };
      this.persistState();
      var focusTarget = this.renderStep(0);
      this.updateFabState();
      if (this.isOpen) this.applyFocusTrap(focusTarget);
    }

    /* ---------------------------------------------------------------- */
    /* Delegated element-scoped events                                   */
    /* ---------------------------------------------------------------- */

    handleClick(e) {
      if (
        e.target.closest('[data-willitfit-close]') ||
        e.target.closest('[data-willitfit-done]')
      ) {
        // "Done" is the customer's explicit "these are my numbers". The
        // results screen already announced on render, so a configurator on
        // the page has almost certainly acted; announcing again is the
        // deliberate confirmation, and consumers are required to be
        // idempotent because of exactly this pair. Close (X) and the overlay
        // are NOT a confirmation and stay silent.
        if (e.target.closest('[data-willitfit-done]')) {
          this.announceResult('done');
        }
        this.closeModal();
        return;
      }
      if (e.target.closest('[data-willitfit-back]')) {
        this.onBack();
        return;
      }
      if (e.target.closest('[data-willitfit-next]')) {
        this.onNext();
        return;
      }
      if (e.target.closest('[data-willitfit-restart]')) {
        this.onRestart();
        return;
      }
      var jambBtn = e.target.closest('[data-willitfit-jamb-choice]');
      if (jambBtn) {
        this.onSelectJamb(jambBtn);
        return;
      }
      var handingBtn = e.target.closest('[data-willitfit-handing-choice]');
      if (handingBtn) {
        this.onSelectHanding(handingBtn);
        return;
      }
      if (e.target.closest('[data-willitfit-pause-previews]')) {
        this.togglePreviews();
        return;
      }
      var mountBtn = e.target.closest('[data-willitfit-mount]');
      if (mountBtn) {
        this.onChooseMount(mountBtn.getAttribute('data-willitfit-mount'));
        return;
      }
      var pathBtn = e.target.closest('[data-willitfit-path]');
      if (pathBtn) {
        this.onChoosePath(pathBtn.getAttribute('data-willitfit-path'));
      }
    }

    handleInput(e) {
      var wholeEl = e.target.closest ? e.target.closest('[data-willitfit-whole]') : null;
      if (!wholeEl) return;
      var measure = wholeEl.getAttribute('data-willitfit-whole');
      var sanitized = wholeEl.value.replace(/\D/g, '').slice(0, 3);
      if (sanitized !== wholeEl.value) wholeEl.value = sanitized;
      this.hideError(measure);
      this.updateNextButtonState(measure);
      this.updateSoftNote(measure);
    }

    handleChange(e) {
      var fracEl = e.target.closest ? e.target.closest('[data-willitfit-frac]') : null;
      if (!fracEl) return;
      var measure = fracEl.getAttribute('data-willitfit-frac');
      this.updateNextButtonState(measure);
      this.updateSoftNote(measure);
    }
  }

  if (!window.customElements.get('will-it-fit')) {
    window.customElements.define('will-it-fit', WillItFitEl);
  }
})();
